import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import { PersistenceLayerInterface } from './PersistenceLayer';
import { createPersistentCache, PersistentCacheInterface } from './PersistentCache';

export const createPersistentCacheContext = (tableName: string) => {
  const PersistentCacheContext = createContext<PersistentCacheInterface | undefined>(undefined);

  /**
   * Provider component for persistent cache context.
   * Accepts an optional persistence layer, defaults to in-memory storage.
   */
  const PersistentCacheProvider = ({
    persistenceLayer,
    children,
  }: {
    persistenceLayer?: PersistenceLayerInterface;
    children: ReactNode;
  }) => {
    const cache = useMemo(() => createPersistentCache(persistenceLayer), [persistenceLayer]);

    useEffect(() => cache.syncAcrossWindows(), [cache]);

    return (
      <PersistentCacheContext.Provider value={cache}>
        {children}
      </PersistentCacheContext.Provider>
    );
  };

  /**
   * Hook to access the persistent cache from any component
   */
  const usePersistentCache = <T extends unknown>(): PersistentCacheInterface<T> => {
    const cache = useContext(PersistentCacheContext) as PersistentCacheInterface<T>;
    if (!cache) {
      throw new Error('usePersistentCache must be used within a PersistentCacheProvider');
    }
    return cache;
  };

  return {
    PersistentCacheProvider,
    usePersistentCache,
  };
};

export const { PersistentCacheProvider, usePersistentCache } = createPersistentCacheContext('persistent-cache');
